/**
 * Relays a chat stream's frames, calling onFrame for each one and onStall when stallMs pass
 * without one. The timer runs only while the stream is being read and stops when it ends.
 *
 * While a turn is live the server sends a frame at least every ~15s (an empty keepalive when there
 * is nothing else), so a stream that stays quiet past the stall window has died without closing: a
 * proxy dropped the connection, or iOS Safari suspended the fetch in the background. Neither
 * surfaces as an error or an end, which is all the reader would otherwise notice.
 */
export async function* watchStream<T>(
  source: AsyncIterable<T>,
  stallMs: number,
  onFrame: () => void,
  onStall: () => void
): AsyncGenerator<T> {
  // One timer per stall window rather than one per frame, which arrive at up to ~200/s: when it
  // fires early it re-arms for what is left of the window since the latest frame.
  let lastFrameAt = Date.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const check = () => {
    const quietMs = Date.now() - lastFrameAt;
    if (quietMs < stallMs) {
      timer = setTimeout(check, stallMs - quietMs);
      return;
    }
    timer = undefined;
    onStall();
  };
  timer = setTimeout(check, stallMs);
  try {
    for await (const frame of source) {
      lastFrameAt = Date.now();
      timer ??= setTimeout(check, stallMs);
      onFrame();
      yield frame;
    }
  } finally {
    clearTimeout(timer);
  }
}
